import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  RadialBar,
  RadialBarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const AXIS = { fontSize: 11, fill: "oklch(0.36 0.04 275)" } as const;
const GRID = "oklch(0.9 0.014 270)";

export const chartPalette = [
  "var(--maroon)",
  "var(--saffron-deep)",
  "var(--success)",
  "#5b6ee1",
  "#c084fc",
  "#ef4444",
  "#0ea5e9",
];

function CustomTooltip({ active, payload, label, valueFormatter }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-border bg-white/95 px-3 py-2 text-xs shadow-lg backdrop-blur">
      {label !== undefined && <p className="mb-1 font-semibold text-foreground">{label}</p>}
      {payload.map((p: any) => (
        <div key={p.dataKey ?? p.name} className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-sm" style={{ background: p.color ?? p.fill }} />
          <span className="text-foreground/70">{p.name}:</span>
          <span className="font-semibold text-foreground">
            {valueFormatter ? valueFormatter(p.value) : p.value}
          </span>
        </div>
      ))}
    </div>
  );
}

export function TrendAreaChart({
  data,
  xKey = "label",
  yKey = "value",
  height = 220,
  color = "var(--maroon)",
  valueFormatter,
}: {
  data: any[];
  xKey?: string;
  yKey?: string;
  height?: number;
  color?: string;
  valueFormatter?: (v: number) => string;
}) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={data} margin={{ top: 10, right: 8, left: -10, bottom: 0 }}>
        <defs>
          <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.35} />
            <stop offset="100%" stopColor={color} stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
        <XAxis dataKey={xKey} tick={AXIS} axisLine={false} tickLine={false} />
        <YAxis
          tick={AXIS}
          axisLine={false}
          tickLine={false}
          width={50}
          tickFormatter={valueFormatter}
        />
        <Tooltip
          content={<CustomTooltip valueFormatter={valueFormatter} />}
          cursor={{ stroke: color, strokeOpacity: 0.15 }}
        />
        <Area
          type="monotone"
          dataKey={yKey}
          stroke={color}
          strokeWidth={2.5}
          fill="url(#trendFill)"
          name="Amount"
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

export function ComboBarLineChart({
  data,
  height = 240,
  valueFormatter,
}: {
  data: { label: string; donations: number; donors: number }[];
  height?: number;
  valueFormatter?: (v: number) => string;
}) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 10, right: 8, left: -10, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={GRID} vertical={false} />
        <XAxis dataKey="label" tick={AXIS} axisLine={false} tickLine={false} />
        <YAxis
          yAxisId="left"
          tick={AXIS}
          axisLine={false}
          tickLine={false}
          width={50}
          tickFormatter={valueFormatter}
        />
        <YAxis
          yAxisId="right"
          orientation="right"
          tick={AXIS}
          axisLine={false}
          tickLine={false}
          width={30}
        />
        <Tooltip
          content={<CustomTooltip valueFormatter={valueFormatter} />}
          cursor={{ fill: "oklch(0.97 0.008 270 / 0.6)" }}
        />
        <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} iconType="circle" />
        <Bar
          yAxisId="left"
          dataKey="donations"
          fill="var(--maroon)"
          name="Donations ₹"
          radius={[6, 6, 0, 0]}
          maxBarSize={28}
        />
        <Line
          yAxisId="right"
          type="monotone"
          dataKey="donors"
          stroke="var(--saffron-deep)"
          strokeWidth={2.5}
          dot={{ r: 3, fill: "var(--saffron-deep)" }}
          name="Donors"
        />
      </BarChart>
    </ResponsiveContainer>
  );
}

export function DonutChart({
  data,
  height = 220,
  valueFormatter,
}: {
  data: { name: string; value: number }[];
  height?: number;
  valueFormatter?: (v: number) => string;
}) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Tooltip content={<CustomTooltip valueFormatter={valueFormatter} />} />
        <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" verticalAlign="bottom" />
        <Pie
          data={data}
          dataKey="value"
          nameKey="name"
          innerRadius={55}
          outerRadius={85}
          paddingAngle={2}
          strokeWidth={0}
        >
          {data.map((_, i) => (
            <Cell key={i} fill={chartPalette[i % chartPalette.length]} />
          ))}
        </Pie>
      </PieChart>
    </ResponsiveContainer>
  );
}

export function HBarChart({
  data,
  height = 240,
  valueFormatter,
}: {
  data: { label: string; value: number }[];
  height?: number;
  valueFormatter?: (v: number) => string;
}) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} layout="vertical" margin={{ top: 4, right: 12, left: 4, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={GRID} horizontal={false} />
        <XAxis
          type="number"
          tick={AXIS}
          axisLine={false}
          tickLine={false}
          tickFormatter={valueFormatter}
        />
        <YAxis
          type="category"
          dataKey="label"
          tick={{ ...AXIS, fill: "oklch(0.22 0.06 278)" }}
          axisLine={false}
          tickLine={false}
          width={140}
        />
        <Tooltip
          content={<CustomTooltip valueFormatter={valueFormatter} />}
          cursor={{ fill: "oklch(0.97 0.008 270 / 0.6)" }}
        />
        <Bar dataKey="value" name="Raised" radius={[0, 6, 6, 0]} maxBarSize={18}>
          {data.map((_, i) => (
            <Cell key={i} fill={chartPalette[i % chartPalette.length]} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

export function RadialGauge({
  value,
  label,
  hint,
  color = "var(--maroon)",
  height = 180,
}: {
  value: number;
  label: string;
  hint?: string;
  color?: string;
  height?: number;
}) {
  const data = [{ name: label, value, fill: color }];
  return (
    <div className="relative">
      <ResponsiveContainer width="100%" height={height}>
        <RadialBarChart
          innerRadius="70%"
          outerRadius="100%"
          data={data}
          startAngle={210}
          endAngle={-30}
        >
          <RadialBar
            dataKey="value"
            background={{ fill: "oklch(0.94 0.014 270)" }}
            cornerRadius={12}
          />
        </RadialBarChart>
      </ResponsiveContainer>
      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
        <p className="font-display text-2xl font-bold text-foreground">{value}%</p>
        <p className="text-[10px] uppercase tracking-wider text-foreground/70">{label}</p>
        {hint && <p className="mt-0.5 text-[10px] text-foreground/60">{hint}</p>}
      </div>
    </div>
  );
}

export function SparkLine({
  data,
  color = "var(--maroon)",
  height = 40,
}: {
  data: number[];
  color?: string;
  height?: number;
}) {
  const rows = data.map((v, i) => ({ i, v }));
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={rows}>
        <Line type="monotone" dataKey="v" stroke={color} strokeWidth={2} dot={false} />
      </LineChart>
    </ResponsiveContainer>
  );
}
